import Route from '@ember/routing/route';
import { service } from '@ember/service';

import type { ConnectWrapperObject } from '@databox/ember-integrations/lib/components/Connect/Connect';

import type { Service } from 'webapp-types';

import type AppDataLoaderMain from 'webapp/data-loader/main';

type ExternalInstallStep = 'authorize' | 'finalize';

interface ExternalInstallParams {
	group: string;
	step: ExternalInstallStep;
	returnUrl: string;
	code?: string;
	state?: string;
}

const VALID_STEPS: ExternalInstallStep[] = ['authorize', 'finalize'];
const CONNECT_FUNCTIONS_POLL_MS = 50;
const CONNECT_FUNCTIONS_TIMEOUT_MS = 15000;

export default class DataManagerExternalInstallGroupRoute extends Route {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('connect') declare connectService: Service.Connect;

	override model(params: { group: string }): { group: string } {
		return { group: params.group };
	}

	override afterModel(model: { group: string }): void {
		const popup = this.navigationService.createFullscreenLoaderModal();
		popup.open({ text: 'Completing installation...' });

		const queryString = this.resolveQueryString();
		const parsed = this.parseQueryParams(model.group, queryString);

		if (!parsed) {
			void popup.close();
			this.navigationService.replaceRoute('datamanager.available');
			return;
		}

		this.awaitConnectFunctions()
			.then((connectFunctions) => {
				connectFunctions.externalInstall(parsed);
			})
			.catch(() => {
				void popup.close();
				this.navigationService.replaceRoute('datamanager.available');
			});
	}

	private resolveQueryString(): string {
		if (window.location.search) {
			return window.location.search;
		}

		// Post-login redirect: the data loader stashes the original URL.
		const externalInstallUrl = (this.navigationService.dataLoader as AppDataLoaderMain)
			.externalInstallUrl;
		const [, stashed] = externalInstallUrl.split('?');
		return stashed ? `?${stashed}` : '';
	}

	private parseQueryParams(group: string, queryString: string): ExternalInstallParams | null {
		const params = new URLSearchParams(queryString);
		const step = params.get('step');
		const returnUrl = params.get('returnUrl');

		if (!step || !VALID_STEPS.includes(step as ExternalInstallStep) || !returnUrl) {
			return null;
		}

		const result: ExternalInstallParams = {
			group,
			step: step as ExternalInstallStep,
			returnUrl,
		};

		const code = params.get('code');
		const state = params.get('state');

		if (code) {
			result.code = code;
		}
		if (state) {
			result.state = state;
		}

		return result;
	}

	private awaitConnectFunctions(): Promise<ConnectWrapperObject> {
		if (this.connectService.connectFunctions) {
			return Promise.resolve(this.connectService.connectFunctions);
		}

		return new Promise<ConnectWrapperObject>((resolve, reject) => {
			const deadline = Date.now() + CONNECT_FUNCTIONS_TIMEOUT_MS;
			const interval = setInterval(() => {
				if (this.connectService.connectFunctions) {
					clearInterval(interval);
					resolve(this.connectService.connectFunctions);
					return;
				}
				if (Date.now() > deadline) {
					clearInterval(interval);
					reject(new Error('Connect functions not available'));
				}
			}, CONNECT_FUNCTIONS_POLL_MS);
		});
	}
}
